import { ConsentTrackingStatus } from "@tandemdiabetes/sugarmate-api-react-client"
import {
  ActionSubmit,
  Form,
  Checkbox,
  CheckboxCheckMark,
  LinkOutlineIcon,
} from "@tandemdiabetes/sugarmate-react-components"
import type {
  ChangeEventHandler,
  FormEventHandler,
  ReactNode,
} from "react"
import { useCallback } from "react"

import styles from "../AccountForms.module.scss"
import customStyles from "./NoticeOfPrivacyPracticeForm.module.scss"

import { usePolyglot } from "src/contexts"

const nppId = "notice-of-privacy-practice-input"
const polyglotPrefix = "components.account_forms.notice_of_privacy_practice."

interface NoticeOfPrivacyPractice {
  nppConsent: ConsentTrackingStatus;
}

interface Props {
  form: NoticeOfPrivacyPractice;
  updateForm: (form: Partial<NoticeOfPrivacyPractice>) => void;
  onSubmit: FormEventHandler<HTMLFormElement>;
  children?: ReactNode;
}

export function NoticeOfPrivacyPracticeForm(props: Props) {
  const {
    onSubmit,
    form,
    updateForm,
    children,
  } = props

  const polyglot = usePolyglot()

  const onChange: ChangeEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      updateForm({
        nppConsent: e.target.checked ? ConsentTrackingStatus.Accepted : ConsentTrackingStatus.Declined,
      })
    },
    [updateForm],
  )

  return (
    <Form
      className={styles.form}
      onSubmit={onSubmit}
    >
      <div className={styles.heading}>
        {polyglot.t(`${polyglotPrefix}title`)}
      </div>
      <div className={styles.description}>
        {polyglot.t(`${polyglotPrefix}description`)}
      </div>
      <div
        className={customStyles.checkboxContainer}
        data-checked={form.nppConsent === ConsentTrackingStatus.Accepted}
      >
        <Checkbox
          className={customStyles.checkbox}
          htmlFor={nppId}
          checked={form.nppConsent === ConsentTrackingStatus.Accepted}
          onChange={onChange}
        >
          <CheckboxCheckMark />
          {polyglot.t(`${polyglotPrefix}acknowledgement`)}
        </Checkbox>
        <a
          href="https://www.tandemdiabetes.com/legal/privacy/hipaa-notice-of-privacy-practices"
          target="_blank"
          rel="noreferrer"
          className={styles.checkboxLink}
        >
          <LinkOutlineIcon className={styles.checkboxLinkIcon} />
        </a>
      </div>
      {form.nppConsent === ConsentTrackingStatus.Accepted ? children : (
        <ActionSubmit
          className={styles.submit}
          stylePreset="secondary"
          value={polyglot.t(`${polyglotPrefix}skip_cta`)}
        />
      )}
    </Form>
  )
}
